import { type IBasicTextFieldMask, TextFieldMask } from "@inputs/text/TextFieldMask";
import type { TextFieldProps, TextFieldVariants } from "@mui/material";
import { useMultiRefs } from "@root/utils/hooks/useMultiRef";
import type { FieldPath, FieldValues, UseControllerReturn } from "react-hook-form";

export type TTextFieldMaskFormExternal<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  Variant extends TextFieldVariants,
> = IBasicTextFieldMask &
  Omit<TextFieldProps<Variant>, "name" | "value" | "onChange" | "onBlur" | "SelectProps" | "select" | "onFocus"> &
  UseControllerReturn<TFieldValues, TName> & {
    hideHelperText?: boolean;
  };

export const TextFieldMaskFormExternal = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  Variant extends TextFieldVariants,
>({
  field,
  fieldState,
  error,
  helperText,
  hideHelperText,
  inputRef,
  ...props
}: TTextFieldMaskFormExternal<TFieldValues, TName, Variant>) => {
  const multiRefs = useMultiRefs(field.ref, inputRef);
  return (
    <TextFieldMask
      {...props}
      InputLabelProps={{
        ...props.InputLabelProps,
        ...(props.disabled && { shrink: true }),
      }}
      name={field.name}
      value={field.value ?? ""}
      onAccept={field.onChange}
      onBlur={field.onBlur}
      inputRef={multiRefs}
      error={error ? true : Boolean(fieldState.error)}
      helperText={hideHelperText !== true ? (helperText ?? fieldState.error?.message) : undefined}
    />
  );
};
